import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { AssetMappingConfig, AssetFieldConfig } from 'src/app/core/models/asset-config.model';
import { faSearch, faTimes, faSignal, faDatabase, faCogs, faCircle, faEdit, faSave, faChevronRight, faChevronDown, faDesktop, faBuilding, faWrench, faQuestionCircle, faEye, faRuler } from '@fortawesome/free-solid-svg-icons';
import { MultiLangField, setMultiLanguageField } from '../../customization/multilang-input/multi-language';
import { IotAssetsService } from 'src/app/core/services/iotAssets.service';
import { LoggerService } from 'src/app/core/services/logger.service';
import { TranslateService } from '@ngx-translate/core';
import { getSignalsCatalog } from 'src/app/core/store/selectors';
import { AppStateInterface } from 'src/app/appState.interface';

interface SignalAttribute {
  name: string;
  label: string;
  type: string;
  unit: string | null;
  scope: string;
  source: 'common' | 'model';
  availableInModels?: string[];
  visible?: boolean;
  kpi?: boolean;
  i18nLabel?: { [key: string]: string };
  isNew?: boolean; // Segnale nuovo non presente nella config salvata
  uid?: string; // Identificatore univoco, utile per custom KPIs
}

@Component({
  selector: 'app-signals-catalog',
  templateUrl: './signals-catalog.component.html',
  styleUrls: ['./signals-catalog.component.scss']
})
export class SignalsCatalogComponent implements OnInit, OnDestroy, OnChanges {
  /** Scope esclusi dal catalogo segnali (gestiti nel tab Work Data) */
  private static readonly EXCLUDED_SCOPES: string[] = ['kpi_custom']//[ 'composite_kpi',];

  private destroy$ = new Subject<void>();

  // Input dal parent component (opzionale, per audience)
  @Input() mappingConfig: AssetMappingConfig | null = null;

  // Catalog data dallo store
  signalsCatalogData: { [scope: string]: any[] } = {};

  // Icons
  faSearch = faSearch;
  faTimes = faTimes;
  faSignal = faSignal;
  faDatabase = faDatabase;
  faCogs = faCogs;
  faCircle = faCircle;
  faEdit = faEdit;
  faSave = faSave;
  faChevronRight = faChevronRight;
  faChevronDown = faChevronDown;
  faDesktop = faDesktop;
  faBuilding = faBuilding;
  faWrench = faWrench;
  faQuestionCircle = faQuestionCircle;
  faEye = faEye;
  faRuler = faRuler;

  // Data
  commonAttributes: SignalAttribute[] = [];
  modelAttributes: Map<string, SignalAttribute[]> = new Map();
  filteredCommonAttributes: SignalAttribute[] = [];
  filteredModelAttributes: Map<string, SignalAttribute[]> = new Map();

  // Attributi raggruppati per scope (per accordion)
  filteredAttributesByScope: { [scope: string]: SignalAttribute[] } = {};

  // Loading & Error states
  isLoading = false;
  errorMessage: string | null = null;

  // Search
  searchQuery = '';

  // Filters
  selectedScope = '';
  availableScopes: string[] = [];

  // Inline editing
  editingKey: string | null = null; // formato: "name|source"
  editingLabelField: MultiLangField | null = null;
  isSaving = false;

  // Stati accordion per i gruppi scope
  expandedGroups: { [scope: string]: boolean } = {};

  // Ordine degli scope (per eventuale drag&drop futuro)
  scopeOrder: string[] = [];

  // Modifiche pending da salvare (key = fieldName) - salviamo l'intera struttura del segnale
  private pendingChanges: Map<string, SignalAttribute> = new Map();

  @Input() selectedAudience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER';

  constructor(
    private iotAssetsService: IotAssetsService,
    private logger: LoggerService,
    private translateService: TranslateService,
    private store: Store<AppStateInterface>
  ) {}

  ngOnInit(): void {
    console.log('📊 [SignalsCatalog] ngOnInit');
    console.log('📊 [SignalsCatalog] Icons loaded:', { faEdit: this.faEdit, faSave: this.faSave });

    // Leggi dati dallo store
    this.store.pipe(
      select(getSignalsCatalog),
      takeUntil(this.destroy$)
    ).subscribe((catalogData) => {
      console.log('📊 [SignalsCatalog] Dati ricevuti dallo store:', catalogData);
      if (catalogData) {
        this.signalsCatalogData = catalogData;

        this.processCatalogData();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Ricarica quando cambia l'audience selezionato
    /* if (changes['selectedAudience'] && !changes['selectedAudience'].firstChange) {
      console.log('🔄 [SignalsCatalog] Audience cambiato, riprocesso dati:', this.selectedAudience);
      if (this.signalsCatalogData && Object.keys(this.signalsCatalogData).length > 0) {
        this.processCatalogData();
      }
    } */
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Processa i dati del catalog dallo store e fa il merge con la config salvata
   */
  private processCatalogData(): void {
    if (!this.signalsCatalogData || Object.keys(this.signalsCatalogData).length === 0) {
      console.warn('⚠️ [SignalsCatalog] Nessun dato disponibile nel catalog');
      this.errorMessage = 'Nessun dato disponibile';
      return;
    }

    console.log('🔄 [SignalsCatalog] Processando catalog data');

    this.isLoading = true;
    this.errorMessage = null;

    // Carica la configurazione salvata per l'audience corrente
    this.iotAssetsService.loadCatalogConfiguration(this.selectedAudience).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result) => {
        console.log('📥 [SignalsCatalog] Configurazione salvata caricata:', result);
        this.mergeCatalogWithSavedConfig(result.data);
      },
      error: (error) => {
        console.error('❌ [SignalsCatalog] Errore caricamento configurazione salvata:', error);
        // Continua comunque con i dati base senza merge
        this.mergeCatalogWithSavedConfig(null);
      }
    });
  }

  /**
   * Fa il merge tra il catalogo base e la configurazione salvata
   */
  private mergeCatalogWithSavedConfig(savedConfig: { [key: string]: SignalAttribute } | null): void {
    this.commonAttributes = [];
    this.modelAttributes.clear();

    // Processa tutti i segnali raggruppati per scope
    Object.entries(this.signalsCatalogData).forEach(([scope, signals]) => {
      if (Array.isArray(signals)) {
        signals.forEach((signal: any) => {
          const attribute: SignalAttribute = {
            name: signal.name || signal.signal || 'unknown',
            label: signal.label || signal.name || 'Unknown',
            type: signal.catalogType || signal.type || 'unknown',
            unit: signal.unit || null,
            scope: signal.scope || scope,
            source: signal.source || 'common',
            availableInModels: signal.availableInModels,
            uid: signal.uid || undefined, // Se disponibile, altrimenti undefined
          };

          // 🔀 MERGE: Applica la configurazione salvata se esiste
          if (savedConfig && savedConfig[attribute.name]) {
            const saved = savedConfig[attribute.name];
            console.log(`🔀 [SignalsCatalog] Merge per ${attribute.name}:`, saved);

            // Applica i valori salvati
            if (saved.visible !== undefined) attribute.visible = saved.visible;
            if (saved.kpi !== undefined) attribute.kpi = saved.kpi;
            if (saved.i18nLabel) attribute.i18nLabel = saved.i18nLabel;

            // NON aggiorniamo label - rimane quello originale dal catalog
            // La visualizzazione userà getDisplayLabel() che mostra i18nLabel se presente

            // NON è nuovo (era già salvato)
            attribute.isNew = false;
          } else {
            // 🆕 Segnale nuovo (non presente nella configurazione salvata)
            attribute.isNew = savedConfig !== null; // Solo se c'è una config salvata
            if (attribute.isNew) {
              console.log(`🆕 [SignalsCatalog] Segnale nuovo: ${attribute.name}`);
            }
          }

          // Distingui tra attributi comuni e specifici dei modelli
          if (signal.source === 'model' && signal.availableInModels && signal.availableInModels.length > 0) {
            signal.availableInModels.forEach((model: string) => {
              if (!this.modelAttributes.has(model)) {
                this.modelAttributes.set(model, []);
              }
              this.modelAttributes.get(model)!.push(attribute);
            });
          } else {
            this.commonAttributes.push(attribute);
          }
        });
      }
    });

    console.log('✅ [SignalsCatalog] Common attributes:', this.commonAttributes.length);
    console.log('✅ [SignalsCatalog] Model attributes:', this.modelAttributes.size, 'modelli');

    this.extractAvailableScopes();
    this.applyFilters();
    this.isLoading = false;
  }

  /**
   * Maps original scopes to aggregated/normalized scopes
   */
  private normalizeScope(originalScope: string): string {
    const scopeMapping: { [key: string]: string } = {
      // Aggregate platform-related scopes
      'kpi': 'platform',
      'composite_kpi': 'platform',
      'platform': 'platform',
      'table': 'platform',
      'composite_signal': 'platform',
      
      // Keep signal as-is
      'signal': 'signal',
      
      // Keep tcu as-is
      'tcu': 'tcu',
      
      // Keep erp as-is
      'erp': 'erp',
      
      // Keep other scopes
      'common': 'common',
      'model': 'model',
      'kpi_custom': 'kpi_custom',
      'other': 'other'
    };
    
    return scopeMapping[originalScope] || originalScope;
  }

  /**
   * Estrae gli scope disponibili dai dati
   */
  private extractAvailableScopes(): void {
    const scopesSet = new Set<string>();

    // Dagli attributi comuni
    this.commonAttributes.forEach(attr => {
      if (attr.scope) {
        const normalized = this.normalizeScope(attr.scope);
        scopesSet.add(normalized);
      }
    });

    // Dagli attributi dei modelli
    this.modelAttributes.forEach(attrs => {
      attrs.forEach(attr => {
        if (attr.scope) {
          const normalized = this.normalizeScope(attr.scope);
          scopesSet.add(normalized);
        }
      });
    });

    this.availableScopes = Array.from(scopesSet).filter(s => !SignalsCatalogComponent.EXCLUDED_SCOPES.includes(s)).sort();
    console.log('📋 [SignalsCatalog] Scope disponibili:', this.availableScopes);
  }

  /**
   * Applica i filtri ai dati e raggruppa per scope
   */
  applyFilters(): void {
    const query = this.searchQuery.toLowerCase().trim();
    const scopeFilter = this.selectedScope;

    // Filtra attributi comuni
    this.filteredCommonAttributes = this.commonAttributes.filter(attr => {
      const matchesSearch = !query ||
        attr.name.toLowerCase().includes(query) ||
        attr.label.toLowerCase().includes(query) ||
        (attr.type && attr.type.toLowerCase().includes(query));

      const matchesScope = !scopeFilter || attr.scope === scopeFilter;

      return matchesSearch && matchesScope;
    });

    // Filtra attributi dei modelli
    this.filteredModelAttributes.clear();
    this.modelAttributes.forEach((attrs, modelName) => {
      const filteredAttrs = attrs.filter(attr => {
        const matchesSearch = !query ||
          attr.name.toLowerCase().includes(query) ||
          attr.label.toLowerCase().includes(query) ||
          (attr.type && attr.type.toLowerCase().includes(query));

        const matchesScope = !scopeFilter || attr.scope === scopeFilter;

        return matchesSearch && matchesScope;
      });

      if (filteredAttrs.length > 0) {
        this.filteredModelAttributes.set(modelName, filteredAttrs);
      }
    });

    // Raggruppa tutti gli attributi per scope
    this.filteredAttributesByScope = {};
    const allFilteredAttributes: SignalAttribute[] = [
      ...this.filteredCommonAttributes
    ];

    // Aggiungi gli attributi dei modelli
    this.filteredModelAttributes.forEach(attrs => {
      allFilteredAttributes.push(...attrs);
    });

    allFilteredAttributes.forEach(attr => {
      const originalScope = attr.scope || 'other';
      if (SignalsCatalogComponent.EXCLUDED_SCOPES.includes(originalScope)) return;
      
      // Usa lo scope normalizzato per aggregare multiple scope
      const normalizedScope = this.normalizeScope(originalScope);
      
      if (!this.filteredAttributesByScope[normalizedScope]) {
        this.filteredAttributesByScope[normalizedScope] = [];
      }
      this.filteredAttributesByScope[normalizedScope].push(attr);
    });

    // Ordina i segnali per nome dentro ogni scope
    Object.keys(this.filteredAttributesByScope).forEach(scope => {
      this.filteredAttributesByScope[scope].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
    });

    // Aggiorna l'ordine degli scope (alfabetico)
    this.scopeOrder = Object.keys(this.filteredAttributesByScope).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );

    console.log('🔍 [SignalsCatalog] Filtri applicati - Scope:', this.scopeOrder, 'Total:', allFilteredAttributes.length);
  }


  /**
   * Gestisce il cambio di ricerca
   */
  onSearchChange(): void {
    this.applyFilters();
  }

  /**
   * Pulisce la ricerca
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilters();
  }

  /**
   * Gestisce il cambio di filtro scope
   */
  onScopeFilterChange(): void {
    this.applyFilters();
  }

  /**
   * Ritorna l'icona per lo scope (identico a general-setting)
   */
  getScopeIcon(scope: string): any {
    const iconMap: { [key: string]: any } = {
      'signal': this.faSignal,
      'tcu': this.faCogs,
      'platform': this.faDesktop,
      'erp': this.faBuilding,
      'common': this.faWrench,
      'model': this.faCogs,
      'kpi': this.faCogs,
      'kpi_custom': this.faCogs,
      'other': this.faQuestionCircle,
      'general': this.faQuestionCircle
    };
    return iconMap[scope] || this.faQuestionCircle;
  }

  /**
   * Ritorna la classe CSS per lo scope
   */
  getScopeClass(scope: string): string {
    return `scope-${scope}`;
  }

  /**
   * Toggle accordion per i gruppi scope
   */
  toggleGroup(scope: string): void {
    this.expandedGroups[scope] = !this.expandedGroups[scope];
  }

  /**
   * Verifica se un gruppo è espanso
   */
  isGroupExpanded(scope: string): boolean {
    return this.expandedGroups[scope] === true; // default chiuso
  }

  /**
   * TrackBy function per gli scope
   */
  trackByScope(_index: number, scope: string): string {
    return scope;
  }

  /**
   * Conta il numero totale di segnali
   */
  getTotalSignalsCount(): number {
    return Object.values(this.filteredAttributesByScope).reduce((total, attrs) => total + attrs.length, 0);
  }

  /**
   * Conta il numero di segnali nuovi per uno specifico scope
   */
  getNewSignalsCount(scope: string): number {
    if (!this.filteredAttributesByScope[scope]) return 0;
    return this.filteredAttributesByScope[scope].filter(attr => attr.isNew === true).length;
  }

  /**
   * Ritorna il label tradotto per lo scope
   */
  getScopeLabel(scope: string): string {
    const labelMap: { [key: string]: string } = {
      'signal': this.translateService.instant('scopeNames.signal') || 'Signal Data',
      'tcu': this.translateService.instant('scopeNames.tcu') || 'TCU',
      'platform': this.translateService.instant('scopeNames.platform') || 'Platform',
      'erp': this.translateService.instant('scopeNames.erp') || 'ERP',
      'common': this.translateService.instant('scopeNames.common') || 'Common',
      'model': this.translateService.instant('scopeNames.model') || 'Model',
      'kpi': this.translateService.instant('scopeNames.kpi') || 'Work Data',
      'kpi_custom': this.translateService.instant('scopeNames.kpi_custom') || 'Custom Work Data',
      'other': this.translateService.instant('scopeNames.other') || 'Others',
      'general': this.translateService.instant('scopeNames.general') || 'General'
    };
    return labelMap[scope] || scope.toUpperCase();
  }

  /**
   * Ricarica i dati
   */
  retry(): void {
    this.errorMessage = null;
    this.processCatalogData();
  }

  /**
   * Ottiene l'array dei nomi dei modelli filtrati
   */
  getFilteredModelNames(): string[] {
    return Array.from(this.filteredModelAttributes.keys()).sort();
  }

  /**
   * Ottiene gli attributi di un modello filtrato
   */
  getModelAttributes(modelName: string): SignalAttribute[] {
    return this.filteredModelAttributes.get(modelName) || [];
  }

  // ==================== INLINE EDITING ====================

  /**
   * Genera la chiave unica per identificare un attributo
   */
  private getAttributeKey(attr: SignalAttribute): string {
    return `${attr.name}|${attr.source}`;
  }

  /**
   * Verifica se un attributo è in modalità editing
   */
  isEditing(attr: SignalAttribute): boolean {
    return this.editingKey === this.getAttributeKey(attr);
  }

  /**
   * Avvia l'editing inline di un attributo
   */
  startEdit(attr: SignalAttribute): void {
    console.log('✏️ [SignalsCatalog] Start editing:', attr.name);
    this.editingKey = this.getAttributeKey(attr);

    // Inizializza il campo multilingua
    this.editingLabelField = new MultiLangField('name');
    this.editingLabelField.defaultPlaceholder = attr.label;
    setMultiLanguageField(this.editingLabelField);

    // Pre-popola con le traduzioni esistenti se presenti
    if (attr.i18nLabel && this.editingLabelField) {
      Object.entries(attr.i18nLabel).forEach(([langKey, labelValue]) => {
        // langKey formato: 'en-US', 'it-IT', etc.
        const lang = langKey.split('-')[0]; // Estrai 'en', 'it', etc.
        const existingLabel = this.editingLabelField!.labels.find(l => l.lang === lang);
        if (existingLabel) {
          existingLabel.value = labelValue;
        }
      });
      console.log('✏️ [SignalsCatalog] Pre-popolate traduzioni esistenti:', attr.i18nLabel);
    }

    this.logger.debug('signals-catalog', `Avviato editing per: ${attr.name}`);
  }

  /**
   * Annulla l'editing inline
   */
  cancelEdit(): void {
    this.editingKey = null;
    this.editingLabelField = null;
  }

  /**
   * Salva le modifiche localmente (senza chiamare API)
   */
  saveEdit(attr: SignalAttribute): void {
    if (!this.editingLabelField) return;

    this.isSaving = true;
    this.logger.debug('signals-catalog', `Salvataggio locale modifiche per: ${attr.name}`);

    // Costruisci l'oggetto i18nLabel dalle labels multilingua
    const i18nLabel: { [key: string]: string } = {};
    this.editingLabelField.labels.forEach(labelItem => {
      if (labelItem.value && labelItem.value.trim() !== '') {
        const langKey = `${labelItem.lang}-${labelItem.lang.toUpperCase()}`; // 'it' -> 'it-IT'
        i18nLabel[langKey] = labelItem.value;
      }
    });

    console.log('📝 [SignalsCatalog] i18nLabel costruito da multilang-input:', i18nLabel);

    // Aggiorna l'oggetto originale con i18nLabel
    attr.i18nLabel = i18nLabel;

    // NON aggiorniamo attr.label - rimane quello originale
    // La visualizzazione userà getDisplayLabel() per mostrare la traduzione corretta

    console.log('📝 [SignalsCatalog] Attributo aggiornato:', {
      name: attr.name,
      label: attr.label, // rimane originale
      i18nLabel: attr.i18nLabel
    });

    this.logger.info('signals-catalog', `Modifiche salvate localmente per: ${attr.name}`);
    this.isSaving = false;
    this.cancelEdit();
  }

  /**
   * Restituisce TUTTO il catalogo completo da salvare (non solo le modifiche)
   * Questo permette di avere uno snapshot completo per il merge futuro
   */
  public getCatalogChanges(): { [key: string]: SignalAttribute } | null {
    const catalogData: { [key: string]: SignalAttribute } = {};

    // Aggiungi tutti i segnali comuni
    this.commonAttributes.forEach(signal => {
      catalogData[signal.name] = {
        ...signal,
        // Assicura che visible, kpi e i18nLabel siano sempre presenti
        visible: signal.visible !== undefined ? signal.visible : true,
        kpi: signal.kpi !== undefined ? signal.kpi : false,
        i18nLabel: signal.i18nLabel || undefined
      };
    });

    // Aggiungi tutti i segnali specifici per modello
    this.modelAttributes.forEach((signals, modelName) => {
      signals.forEach(signal => {
        catalogData[signal.name] = {
          ...signal,
          // Assicura che visible, kpi e i18nLabel siano sempre presenti
          visible: signal.visible !== undefined ? signal.visible : true,
          kpi: signal.kpi !== undefined ? signal.kpi : false,
          i18nLabel: signal.i18nLabel || undefined
        };
      });
    });

    // CRITICAL: Applica le modifiche da pendingChanges (toggle visible/kpi dagli switch)
    if (this.pendingChanges.size > 0) {
      console.log('📝 [SignalsCatalog] Applicazione di', this.pendingChanges.size, 'modifiche pending al catalog');
      this.pendingChanges.forEach((modifiedSignal, signalName) => {
        if (catalogData[signalName]) {
          // Sovrascrivi con le modifiche dall'utente
          catalogData[signalName] = {
            ...catalogData[signalName],
            ...modifiedSignal,
            // Assicura che le proprietà critiche siano mantenute
            visible: modifiedSignal.visible !== undefined ? modifiedSignal.visible : catalogData[signalName].visible,
            kpi: modifiedSignal.kpi !== undefined ? modifiedSignal.kpi : catalogData[signalName].kpi,
            i18nLabel: modifiedSignal.i18nLabel || catalogData[signalName].i18nLabel
          };
          console.log('✅ [SignalsCatalog] Applicata modifica per:', signalName, {
            visible: catalogData[signalName].visible,
            kpi: catalogData[signalName].kpi
          });
        }
      });
    }

    // Se non ci sono segnali nel catalogo, non salvare nulla
    if (Object.keys(catalogData).length === 0) {
      console.log('📤 [SignalsCatalog] Nessun segnale nel catalogo');
      return null;
    }

    console.log('📤 [SignalsCatalog] Salvataggio catalogo completo:', {
      totalSignals: Object.keys(catalogData).length,
      pendingChangesApplied: this.pendingChanges.size,
      catalogData
    });
    return catalogData;
  }

  /**
   * Resetta le modifiche pending dopo il salvataggio
   */
  public resetPendingChanges(): void {
    this.pendingChanges.clear();
    console.log('🗑️ [SignalsCatalog] Modifiche pending resetate');
  }

  /**
   * Handler per cambio campo multilingua
   */
  onLabelChange(field: MultiLangField): void {
    this.editingLabelField = field;
  }

  /**
   * Ottiene la label da visualizzare per un attributo
   * Se esiste i18nLabel per la lingua corrente, usa quello
   * Altrimenti usa label originale
   */
  getDisplayLabel(attr: SignalAttribute): string {
    if (attr.i18nLabel) {
      const currentLang = localStorage.getItem('language') || 'en';
      const currentLangKey = `${currentLang}-${currentLang.toUpperCase()}`;
      if (attr.i18nLabel[currentLangKey]) {
        return attr.i18nLabel[currentLangKey];
      }
    }
    // Fallback: usa label originale
    return attr.label;
  }

  /**
   * Handler per cambio visible
   */
  onVisibleChange(attr: SignalAttribute, isVisible: boolean): void {
    console.log('👁️ [SignalsCatalog] Cambio visible per:', attr.name, isVisible);

    // Aggiorna visualizzazione locale
    attr.visible = isVisible;

    // Salva l'intera struttura del segnale
    this.pendingChanges.set(attr.name, { ...attr });

    console.log('📝 [SignalsCatalog] Modifica visible memorizzata per:', attr.name);
  }

  /**
   * Handler per cambio kpi
   */
  onKpiChange(attr: SignalAttribute, isKpi: boolean): void {
    console.log('📊 [SignalsCatalog] Cambio kpi per:', attr.name, isKpi);

    // Aggiorna visualizzazione locale
    attr.kpi = isKpi;

    // Salva l'intera struttura del segnale
    this.pendingChanges.set(attr.name, { ...attr });

    console.log('📝 [SignalsCatalog] Modifica kpi memorizzata per:', attr.name);
  }
}
